import React, { Component, useEffect, useMemo, useState, useRef } from "react";
import { flushSync, unstable_batchedUpdates } from "react-dom";
import { Icon } from "@iconify/react";
import "../../../dist/assets/stylesheets/css/recordPatient.css";
import AnimateHeight from "react-animate-height";
import { patientsActions, fetchPatients } from "../../store/patients-slice";

import {
    getCalibrationVideos,
    getCalibrationVideoUrl,
    startRecording,
    stopRecording,
} from "../../util/recordingAPI";
import { deleteTrial, listDownloadedDeviceFiles, updateTrialDate, updateTrialTagFields } from "../../util/trialApi";
import { normalizeTrialId } from "../../util/trialId";
import { deviceFilesFromDownloadPayload, deviceTagsFromDownloadedFiles } from "../../util/devicePosition";
import {
    acknowledgeSessionTransferMessage,
    startSessionTransfer,
    stopSessionTransfer,
    subscribeSessionTransfer,
} from "../../util/sessionTransfer";
import {
    SESSION_SCOPE,
    buildSessionName,
    buildTrialTagPayload,
    defaultTagValues,
    formatTrialTimestamp,
    inheritTrialTags,
} from "../../util/trialTagFields";
import {
    currentAccountKey,
    loadTagMemory,
    rememberTagValue,
} from "../../util/trialTagMemory";
import SessionDetailsCard from "./SessionDetailsCard";
import CalibrationSelection from "./CalibrationSelection";
import { getPatients } from "../../util/patientsApi";
import PatientSelection from "./PatientSelection";
import CalibrationViewModal from "./CalibrationViewModal";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { notificationActions } from "../../store/notification-slice";
import { IoIosArrowBack } from "react-icons/io";
import { useQuery, useSharedResolution } from "../SharedHooks";
import { useHistory } from "react-router-dom";
import RecordingPanel from "./RecordingPanel";
import NoDevicesFound from "./NoDevicesFound";
import ScanningDevicesSpinner from "./ScanningDevicesSpinner";
import AddNewPatientModal from "../patients/AddNewPatientModal";
import DeviceConnectionCheckModal from "./DeviceConnectionCheckModal";
import GreyButton from "../GreyButton";
import Button from '@mui/material/Button';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box } from '@mui/material';
import Fade from '@mui/material/Fade';
import { styled } from '@mui/material/styles';
import { usePiPresence } from "../../hooks/usePiPresence";
import { usePipelineVersion } from "../../hooks/usePipelineVersion";
import { isCalibrationUiEnabled } from "../../util/pipelineVersion";
import { runRecordDeviceConnectionCheck } from "../../util/runRecordDeviceConnectionCheck";

const CommonButton = styled(Button)({
    color: 'black',
    '& svg': {
      fontSize: '1.2rem',
      marginRight: '5px',
    }
  });

/** Debounce for tag edits, so typing in Comments does not POST per keystroke. */
const TAG_SAVE_DELAY = 600;
/** Matches the record dropdowns so a finished session collapses at the same pace. */
const SESSION_LEAVE_MS = 420;
/** The post-upload overlay stays up this long before the fresh trial setup. */
const NEW_SESSION_HOLD_MS = 3000;

/** Scroll the record column so a tag panel's bottom sits inside the window. */
function revealRecordPanel(nodeId) {
    const reveal = () => {
        const node = document.getElementById(nodeId);
        const scroller = node && node.closest('.parent-container');
        if (!node || !scroller) return;
        const rect = node.getBoundingClientRect();
        if (rect.height < 1) return;
        const view = scroller.getBoundingClientRect();
        const pad = 24;
        if (rect.bottom > view.bottom - pad) {
            scroller.scrollTop += rect.bottom - (view.bottom - pad);
        }
        const after = node.getBoundingClientRect();
        const viewAfter = scroller.getBoundingClientRect();
        if (after.top < viewAfter.top + pad && after.height <= viewAfter.height) {
            scroller.scrollTop -= (viewAfter.top + pad) - after.top;
        }
    };
    const timer = setTimeout(reveal, 520);
    return () => clearTimeout(timer);
}

function RecordPage() {
    usePiPresence('record');
    // Pipeline 2.0 calibrates itself, so the page offers no calibration controls.
    const calibrationEnabled = isCalibrationUiEnabled(usePipelineVersion());
    const dispatch = useDispatch();
    const query = useQuery();
    const history = useHistory();
    const { resolution } = useSharedResolution();
    const [trials, setTrials] = useState([]);
    const [calibration, setCalibration] = useState({
        selected: null,
        open: false,
        data: null,
    });
    const patientsList = useSelector((state) => state.patients.patientsList);
    const [patient, setPatient] = useState({
        selected: query.get("selectedPatient") || false,
        open: false,
        data: patientsList || null
    });
    const [showNewPatientModal, setShowNewPatientModal] = useState(null)
    const [calibrationOpen, setCalibrationOpen] = useState(false);
    const [patientOpen, setPatientOpen] = useState(false);

    const [device, setDevice] = useState({
        finishedChecking: false,
        devicesFound: false,
    })
    const [calibrationView, setCalibrationView] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [downloadsComplete, setDownloadsComplete] = useState(false);
    /* The finished session stays on screen while it collapses, then the page is a new trial. */
    const [sessionExit, setSessionExit] = useState(null);
    const sessionExitRef = useRef(false);
    const [readyNotice, setReadyNotice] = useState(false);
    const [startingSession, setStartingSession] = useState(false);
    const newSessionTimerRef = useRef(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [sessionTags, setSessionTags] = useState(() => defaultTagValues(SESSION_SCOPE));
    /*
     * The tag fields ship with no preset entries. Each value becomes a suggestion
     * the first time it is entered, kept per account so one clinician's vocabulary
     * is not offered to another on the same machine.
     */
    const account = useMemo(() => currentAccountKey(), []);
    const [tagMemory, setTagMemory] = useState(() => loadTagMemory(account));
    const [sessionDetailsOpen, setSessionDetailsOpen] = useState(false);
    const [expandedTrialTags, setExpandedTrialTags] = useState(null);
    /* A new trial opens its tag editor at the bottom of a growing list. The
       page scroller is the record column, and the editor's height arrives after
       the open animation, so scroll that column once the panel has its size. */
    useEffect(() => {
        if (expandedTrialTags == null) return undefined;
        return revealRecordPanel(`trial-${expandedTrialTags}-tag-panel`);
    }, [expandedTrialTags, sessionDetailsOpen]);
    useEffect(() => {
        if (!sessionDetailsOpen) return undefined;
        return revealRecordPanel('record-session-details');
    }, [sessionDetailsOpen]);
    const sessionKeyRef = useRef(null);
    const tagSaveTimersRef = useRef({});
    const localIdRef = useRef(0);
    /** Backend id for each card, including one whose start response has not been applied yet. */
    const trialIdsRef = useRef({});
    /** Cards the user already removed. A later start response still deletes that backend row. */
    const discardedRef = useRef(new Set());
    const dismissTimerRef = useRef(null);
    const [connectionCheck, setConnectionCheck] = useState({
        open: true,
        rows: [],
        phase: 'running', // running | success | mismatch | error
        comparison: null,
        evowalkEnabled: localStorage.getItem('evowalkIntegrationEnabled') === 'true',
    });

    const performDeviceCheck = () => {
        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }
        const evowalkIntegrationEnabled = localStorage.getItem('evowalkIntegrationEnabled') === 'true';
        setDevice((prev) => ({ ...prev, finishedChecking: false }));
        setConnectionCheck({
            open: true,
            rows: [],
            phase: 'running',
            comparison: null,
            evowalkEnabled: evowalkIntegrationEnabled,
        });

        runRecordDeviceConnectionCheck({
            evowalkEnabled: evowalkIntegrationEnabled,
            onProgress: (progress) => {
                // Flush each k/N tick so React 18 does not batch the whole
                // GoPro count into one paint after EvoWalk finishes.
                flushSync(() => {
                    setConnectionCheck((prev) => ({
                        ...prev,
                        open: true,
                        rows: progress.rows || prev.rows || [],
                        phase: progress.phase === 'success' ? 'success' : 'running',
                        evowalkEnabled: evowalkIntegrationEnabled,
                    }));
                });
            },
        })
            .then((result) => {
                if (result.success) {
                    setDevice((prev) => ({
                        ...prev,
                        devicesFound: true,
                        finishedChecking: true,
                    }));
                    setConnectionCheck((prev) => ({
                        ...prev,
                        open: true,
                        rows: result.rows || [],
                        phase: 'success',
                        comparison: result.comparison,
                        evowalkEnabled: result.evowalkEnabled,
                    }));
                    dismissTimerRef.current = setTimeout(() => {
                        setConnectionCheck((prev) => ({ ...prev, open: false }));
                        dismissTimerRef.current = null;
                    }, 2200);
                    return;
                }

                setDevice((prev) => ({
                    ...prev,
                    devicesFound: false,
                    finishedChecking: true,
                }));

                if (result.comparison && result.comparison.hasMismatch) {
                    setConnectionCheck((prev) => ({
                        ...prev,
                        open: true,
                        rows: result.rows || [],
                        phase: 'mismatch',
                        comparison: result.comparison,
                        evowalkEnabled: result.evowalkEnabled,
                    }));
                    return;
                }

                setConnectionCheck((prev) => ({
                    ...prev,
                    open: true,
                    rows: result.rows || [],
                    phase: 'error',
                    comparison: result.comparison,
                    evowalkEnabled: result.evowalkEnabled,
                }));
            })
            .catch(() => {
                setDevice((prev) => ({
                    ...prev,
                    devicesFound: false,
                    finishedChecking: true,
                }));
                setConnectionCheck((prev) => ({
                    ...prev,
                    open: true,
                    rows: [],
                    phase: 'error',
                    comparison: null,
                    evowalkEnabled: evowalkIntegrationEnabled,
                }));
            });
    };

    useEffect(() => {
        performDeviceCheck();
        return () => {
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
            }
            if (newSessionTimerRef.current) {
                clearTimeout(newSessionTimerRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (calibrationEnabled) {
            getCalibrationVideos().then((res) =>
                setCalibration({ ...calibration, data: res.result })
            );
        }
        // getPatients().then((res) => setPatient({ ...patient, data: res }));
        dispatch(fetchPatients());
    }, [calibrationEnabled]);
    useEffect(() => {
        setPatient({ ...patient, data: patientsList })
    }, [patientsList])
    const selectCalibration = (selectedCalibration) => {
        localStorage.setItem("calibration", selectedCalibration);
        setCalibration({ ...calibration, open: false, selected: selectedCalibration });

    };
    const selectPatient = (id) => {
        history.push("?selectedPatient=" + id);
        setPatient({ ...patient, open: false, selected: id });
    };
    const selectCalibrationView = (id) => {
        getCalibrationVideoUrl(id)
            .then((res) =>
                setCalibrationView({ videos: res.result, calibration_id: id })
            )
            .catch((e) => {
                dispatch(
                    notificationActions.setNotification({
                        show: true,
                        status: "error",
                        message: e.response.data.reason,
                    })
                );
            });
    };
    // const processAllRecordings = () => {
    //     fetch(window.RECORDING_URL + '/api/finish-session').then(res => res.json()).then(res => console.log(res))
    // }
    const startFreshRecording = () => {
        if (sessionExitRef.current) return;
        sessionExitRef.current = true;
        stopSessionTransfer();
        setReadyNotice(true);
        setSessionExit({ trials });
        setTrials([]);
        setExpandedTrialTags(null);
        setSessionDetailsOpen(false);
        setCalibrationOpen(false);
        setCalibration((prev) => ({ ...prev, selected: null, open: false }));
        localStorage.removeItem('calibration');
        sessionKeyRef.current = null;
    };
    const beginNewSession = () => {
        if (sessionExitRef.current || newSessionTimerRef.current) return;
        stopSessionTransfer();
        setStartingSession(true);
        newSessionTimerRef.current = setTimeout(() => {
            newSessionTimerRef.current = null;
            unstable_batchedUpdates(() => {
                setStartingSession(false);
                startFreshRecording();
            });
        }, NEW_SESSION_HOLD_MS);
    };
    const onSessionExitEnd = (height) => {
        if (height !== 0 || !sessionExitRef.current) return;
        sessionExitRef.current = false;
        setSessionExit(null);
        setDownloadsComplete(false);
        setProcessing(false);
    };
    const processAllRecordings = () => {
        // Upload is done: collapse this session into a fresh trial instead of reloading.
        if (downloadsComplete) {
            beginNewSession();
            return;
        }

        setProcessing(true);
        setDownloadsComplete(false);
        startSessionTransfer({
            trialCount: Math.max(1, trials.filter((trial) => trial.finishedRecording).length),
            evowalkEnabled: connectionCheck.evowalkEnabled,
            recordingUrl: window.RECORDING_URL || '',
        });
    };

    // The poll lives outside this page so the corner notice keeps updating after navigation.
    useEffect(() => {
        return subscribeSessionTransfer((snap) => {
            if (snap.phase === 'running') {
                setProcessing(true);
                setDownloadsComplete(false);
            } else if (snap.phase === 'complete') {
                setProcessing(false);
                setDownloadsComplete(true);
            } else if (snap.phase === 'error') {
                setProcessing(false);
                setDownloadsComplete(false);
            }
            if (snap.message) {
                setDialogMessage(snap.message);
                setOpenDialog(true);
                acknowledgeSessionTransferMessage();
            }
        });
    }, []);

    useEffect(() => {
        return () => {
            Object.values(tagSaveTimersRef.current).forEach((timer) => clearTimeout(timer));
        };
    }, []);

    /*
     * Tags only reach the database once the trial exists there, which happens when
     * Start Recording returns a trial_id. Before that they live in local state and
     * are sent with the first save; after that every edit re-sends them.
     */
    const persistTrialTags = (trial, sessionValues) => {
        if (!trial || trial.trialId == null) return;
        const payload = buildTrialTagPayload({
            sessionTags: sessionValues,
            trialTags: trial.trialTags,
            // The subject is whoever Select Patient chose, not a retyped field.
            subject: trial.patientName,
            trialKey: trial.trialKey,
            sessionKey: trial.sessionKey,
        });
        updateTrialTagFields(trial.trialId, payload).catch((err) =>
            console.error('Failed to save trial tags:', err)
        );
    };

    const scheduleTagSave = (timerKey, save) => {
        if (tagSaveTimersRef.current[timerKey]) clearTimeout(tagSaveTimersRef.current[timerKey]);
        tagSaveTimersRef.current[timerKey] = setTimeout(save, TAG_SAVE_DELAY);
    };

    const rememberTag = (key, value) => {
        const next = rememberTagValue(account, tagMemory, key, value);
        if (next !== tagMemory) setTagMemory(next);
    };

    const handleTrialTagChange = (localId, key, value) => {
        const next = trials.map((trial) => (
            trial.localId === localId ? { ...trial, trialTags: { ...trial.trialTags, [key]: value } } : trial
        ));
        setTrials(next);
        rememberTag(key, value);
        const saved = next.find((trial) => trial.localId === localId);
        scheduleTagSave(`trial-${localId}`, () => persistTrialTags(saved, sessionTags));
    };

    /* Session fields apply to every trial, so editing one re-saves each recorded trial. */
    const handleSessionTagChange = (key, value) => {
        const next = { ...sessionTags, [key]: value };
        setSessionTags(next);
        rememberTag(key, value);
        scheduleTagSave('session', () => {
            trials.forEach((trial) => persistTrialTags(trial, next));
        });
    };

    const reportDeleteFailure = (err) => {
        console.error('Failed to delete trial:', err);
        const data = err && err.response && err.response.data;
        setDialogMessage(
            (data && data.reason)
            || (err && err.message)
            || 'Unable to delete this trial. Please try again.'
        );
        setOpenDialog(true);
    };

    const removeTrial = (localId) => {
        const trial = trials.find((row) => row.localId === localId);
        if (!trial || trial.isDeleting) return;
        const timerKey = `trial-${localId}`;
        if (tagSaveTimersRef.current[timerKey]) {
            clearTimeout(tagSaveTimersRef.current[timerKey]);
            delete tagSaveTimersRef.current[timerKey];
        }
        discardedRef.current.add(localId);
        const id = trial.trialId != null ? String(trial.trialId) : trialIdsRef.current[localId];
        const dropCard = () => {
            setTrials((prev) => prev.filter((row) => row.localId !== localId));
            setExpandedTrialTags((openId) => (openId === localId ? null : openId));
        };
        // Start has not returned an id yet. Drop the card now; the response deletes the row.
        if (id == null) {
            dropCard();
            return;
        }
        setTrials((prev) => prev.map((row) => (
            row.localId === localId ? { ...row, isDeleting: true } : row
        )));
        deleteTrial(id)
            .then(() => {
                const anotherFinished = trials.some((row) => (
                    row.localId !== localId && row.finishedRecording
                ));
                // shortcut: this reads the list from the click. A trial that
                // finishes while the delete request is in flight is not counted.
                if (!anotherFinished) {
                    stopSessionTransfer({ cancelDownload: true });
                    setProcessing(false);
                    setDownloadsComplete(false);
                }
                dropCard();
            })
            .catch((err) => {
                discardedRef.current.delete(localId);
                setTrials((prev) => prev.map((row) => (
                    row.localId === localId ? { ...row, isDeleting: false } : row
                )));
                reportDeleteFailure(err);
            });
    }
    const addNewTrial = () => {
        setReadyNotice(false);
        const previous = trials.length ? trials[trials.length - 1] : null;
        const localId = localIdRef.current;
        localIdRef.current += 1;
        let new_trial = {
            localId,
            assessment: "trial",
            calibrationID: calibration.selected,
            patientID: patient.selected,
            patientName: patient.data.find(el => el.patient_id == patient.selected).patient_name,
            finishedRecording: false,
            isRecording: false,
            isLoading: false,
            recordingStartedAt: null,
            recordingEndedAt: null,
            res: resolution,
            // Consecutive trials usually differ in one or two fields.
            trialTags: inheritTrialTags(previous && previous.trialTags)
        };

        setTrials([...trials, new_trial]);
        setExpandedTrialTags(localId);
        setSessionDetailsOpen(false);
        setPatientOpen(false);
        setCalibrationOpen(false);
        setPatient((prev) => ({ ...prev, open: false }));
        setCalibration((prev) => ({ ...prev, open: false }));
    };
    const addNewPatient = () => {
        setShowNewPatientModal(true)
    }
    const patchTrial = (localId, patch) => {
        setTrials((prev) => {
            if (!prev.some((row) => row.localId === localId)) return prev;
            return prev.map((row) => (row.localId === localId ? { ...row, ...patch } : row));
        });
    };
    const startRecord = (localId) => {
        const trial = trials.find((row) => row.localId === localId);
        if (!trial || trial.isLoading || trial.isRecording || discardedRef.current.has(localId)) return;
        // A finished EvoWalk upload must not leave the button on "Start new recording"
        // while this new trial's cameras are still on the devices.
        stopSessionTransfer();
        setDownloadsComplete(false);
        setProcessing(false);
        const tagsAtStart = trial.trialTags;
        patchTrial(localId, { isLoading: true });
        startRecording(trial.patientID, trial.res, trial.assessment, trial.calibrationID)
            .then(res => {
                // RPi success is often a bare JSON number from jsonify(trial_id).
                const trialId = normalizeTrialId(res);
                if (trialId == null) {
                    const detail =
                        res && typeof res === 'object'
                            ? (res.reason || res.message || JSON.stringify(res))
                            : String(res);
                    throw new Error(
                        `Start recording did not return a trial id (${detail}).`
                    );
                }
                trialIdsRef.current[localId] = trialId;
                // The card was deleted while Starting…. The row already exists and is uploading.
                if (discardedRef.current.has(localId)) {
                    deleteTrial(trialId).catch(reportDeleteFailure);
                    stopRecording().catch((err) => console.error('Error stopping discarded recording:', err));
                    return;
                }
                const recordedAt = new Date();
                const trialKey = formatTrialTimestamp(recordedAt);
                if (!sessionKeyRef.current) sessionKeyRef.current = buildSessionName(recordedAt);
                const sessionKey = sessionKeyRef.current;
                patchTrial(localId, {
                    isRecording: true,
                    isLoading: false,
                    // Same moment the Stop button appears: start-record has returned.
                    recordingStartedAt: Date.now(),
                    recordingEndedAt: null,
                    trialId,
                    trialKey,
                    sessionKey,
                });
                updateTrialDate(trialId, recordedAt).catch(err =>
                    console.error('Failed to update trial date:', err)
                );
                updateTrialTagFields(trialId, buildTrialTagPayload({
                    sessionTags,
                    trialTags: tagsAtStart,
                    subject: trial.patientName,
                    trialKey,
                    sessionKey,
                })).catch(err => console.error('Failed to save trial tags:', err));
            })
            .catch(err => {
                console.error('Error starting recording:', err);
                const failedId = err && err.trialId ? String(err.trialId) : null;
                const gone = discardedRef.current.has(localId);
                // Cameras never started, but create-trial already left an uploading row.
                if (failedId) {
                    trialIdsRef.current[localId] = failedId;
                    deleteTrial(failedId).then(() => {
                        delete trialIdsRef.current[localId];
                    }).catch((deleteErr) => {
                        if (gone) {
                            reportDeleteFailure(deleteErr);
                            return;
                        }
                        patchTrial(localId, { trialId: failedId });
                    });
                }
                if (gone) return;
                patchTrial(localId, { isLoading: false, isRecording: false });
                setDialogMessage(
                    err && err.message
                        ? err.message
                        : 'Unable to start recording. Please try again.'
                );
                setOpenDialog(true);
            });
    };
    const stopRecord = (localId) => {
        setTrials(currentTrials => {
            const index = currentTrials.findIndex((row) => row.localId === localId);
            const trial = index >= 0 ? currentTrials[index] : null;
            if (!trial || trial.isLoading || !trial.isRecording) {
                return currentTrials;
            }
            let copy = JSON.parse(JSON.stringify(currentTrials));
            copy[index].isLoading = true;
            copy[index].recordingEndedAt = Date.now();
            stopRecording()
                .then(async (stopResult) => {
                    // Device tags come from the files this trial just downloaded,
                    // not from the session form.
                    let deviceTags = { device: '', leftpos: '', rightpos: '' };
                    try {
                        const fromStop = deviceFilesFromDownloadPayload(stopResult);
                        const files = fromStop || await listDownloadedDeviceFiles(trial.trialId);
                        deviceTags = deviceTagsFromDownloadedFiles(files);
                    } catch (err) {
                        console.error('Failed to read downloaded device files:', err);
                    }
                    const hasDeviceTag = ['device', 'leftpos', 'rightpos'].some((key) => deviceTags[key]);
                    const nextTags = { ...(trial.trialTags || {}), ...deviceTags };
                    patchTrial(localId, {
                        isRecording: false,
                        finishedRecording: true,
                        isLoading: false,
                        ...(hasDeviceTag ? { trialTags: nextTags } : {}),
                    });
                    if (hasDeviceTag && trial.trialId != null) {
                        persistTrialTags({ ...trial, trialTags: nextTags }, sessionTags);
                    }
                })
                .catch(err => {
                    console.error('Error stopping recording:', err);
                    patchTrial(localId, { isLoading: false, recordingEndedAt: null });
                    setDialogMessage(
                        err && err.message
                            ? err.message
                            : 'Unable to stop recording. Please try again.'
                    );
                    setOpenDialog(true);
                });
            return copy;
        });
    }
    // Lock page actions while waiting on start/stop RPi responses.
    const actionsLocked = trials.some((trial) => trial.isLoading);
    const setFinishRecording = (index) => {
        console.log('done', index)
        let trials_copy = JSON.parse(JSON.stringify(trials))
        trials_copy[index] = { ...trials_copy, finishedRecording: true }
        setTrials(trials_copy)
    }

    const shownTrials = sessionExit ? sessionExit.trials : trials;
    const sessionFinished = shownTrials.length > 0 && shownTrials.every((trial) => trial.finishedRecording);

    return (
        <div className="recordings-page parent-container">
            {readyNotice && (
                <p className="record-session-ready" role="status">
                    {connectionCheck.evowalkEnabled
                        ? 'Cameras and EvoWalk™ are ready'
                        : 'Cameras are ready'}
                </p>
            )}
            <div className="container">
                <h1 className="page-title">Record Page</h1>
                <span className="page-sub-title">
                    {calibrationEnabled
                        ? 'Select calibration and patient to record a new trial.'
                        : 'Select a patient to record a new trial.'}
                </span>
            </div>
            
            {/* Device connection check: full-screen modal with progress (replaces top banner) */}
            <DeviceConnectionCheckModal
                open={connectionCheck.open || startingSession}
                message={startingSession ? 'Starting new session...' : ''}
                rows={connectionCheck.rows}
                phase={connectionCheck.phase}
                comparison={connectionCheck.comparison}
                evowalkEnabled={connectionCheck.evowalkEnabled}
                onRetry={performDeviceCheck}
            />
            
            {/* modal component */}
            {calibrationView?.videos && (
                <CalibrationViewModal
                    handleClose={() => setCalibrationView(null)}
                    data={calibrationView}
                />
            )}
            {showNewPatientModal && <AddNewPatientModal handleClose={() => setShowNewPatientModal(false)} />}

            <Box display="flex" justifyContent="space-between" gap="10px" mb={2}>
                {calibrationEnabled && (
                    <Box display="flex" justifyContent="flex-end" width="100%">
                        {actionsLocked ? (
                            <GreyButton title="Add New Calibration" disabled />
                        ) : (
                            <Link to='/calibration'>
                                <GreyButton title="Add New Calibration" />
                            </Link>
                        )}
                    </Box>
                )}
                <Box display="flex" justifyContent="flex-end" width="100%">
                    <GreyButton title="Add New Patient" onClick={addNewPatient} disabled={actionsLocked} />
                </Box>
            </Box>

            <Box display="flex" justifyContent="space-between" gap="10px" mb={2} alignItems="flex-start">
                {calibrationEnabled && (
                <div className={`calibration-selection-container${calibration.open ? ' open' : ''}${actionsLocked ? ' disabled' : ''}`} style={{ flexBasis: '48%' }}>
                    <div
                        className="step-card"
                        role="button"
                        tabIndex={actionsLocked ? -1 : 0}
                        aria-expanded={calibration.open}
                        aria-disabled={actionsLocked ? 'true' : undefined}
                        aria-controls="record-calibration-dropdown"
                        onClick={() => {
                            if (actionsLocked) return;
                            setCalibration((prev) => ({ ...prev, open: !prev.open }));
                        }}
                        onKeyDown={(e) => {
                            if (actionsLocked) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setCalibration((prev) => ({ ...prev, open: !prev.open }));
                            }
                        }}
                    >
                        <div className="step-card-title-container">
                            {calibration.selected ? (
                                <Icon className="check" icon="ei:check" />
                            ) : (
                                <Icon icon="la:circle-solid" color="gray" />
                            )}
                            <h5>Select Calibration</h5>
                            <span>
                                {calibration.selected && (() => {
                                    const cal = calibration.data?.find((el) => el.id === calibration.selected);
                                    if (!cal) return null;
                                    const datePart = cal.name && cal.name.split(' ').length > 1 ? cal.name.split(' ').slice(1).join(' ') : '';
                                    return datePart ? `ID: ${cal.id} • ${datePart}` : `ID: ${cal.id}`;
                                })()}
                            </span>
                        </div>
                        <Icon
                            icon="iconamoon:arrow-up-2-light"
                            className={calibration?.open ? 'open' : undefined}
                        />
                    </div>
                    <AnimateHeight
                        id="record-calibration-dropdown"
                        className="record-dropdown-animate-height"
                        duration={420}
                        height={calibration.open ? 'auto' : 0}
                        easing="cubic-bezier(0.33, 1, 0.68, 1)"
                        animateOpacity
                    >
                        <CalibrationSelection
                            selectCalibration={selectCalibration}
                            selectCalibrationView={selectCalibrationView}
                            data={calibration.data}
                            selected={calibration.selected}
                        />
                    </AnimateHeight>
                </div>
                )}

                <div className={`patient-selection-container${patient.open ? ' open' : ''}${actionsLocked ? ' disabled' : ''}`} style={{ flexBasis: calibrationEnabled ? '48%' : '100%' }}>
                    <div
                        className="step-card"
                        role="button"
                        tabIndex={actionsLocked ? -1 : 0}
                        aria-expanded={patient.open}
                        aria-disabled={actionsLocked ? 'true' : undefined}
                        aria-controls="record-patient-dropdown"
                        onClick={() => {
                            if (actionsLocked) return;
                            setPatient((prev) => ({ ...prev, open: !prev.open }));
                        }}
                        onKeyDown={(e) => {
                            if (actionsLocked) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPatient((prev) => ({ ...prev, open: !prev.open }));
                            }
                        }}
                    >
                        <div className="step-card-title-container">
                            {patient.selected ? (
                                <Icon className="check" icon="ei:check" />
                            ) : (
                                <Icon icon="la:circle-solid" color="gray" />
                            )}

                            <h5>Select Patient</h5>
                            <span>
                                {patient.selected &&
                                    patient.data &&
                                    patient.data.find((el) => el.patient_id == patient.selected)?.patient_name || 'No patient selected'}
                            </span>
                        </div>
                        <Icon
                            icon="iconamoon:arrow-up-2-light"
                            className={patient?.open ? 'open' : undefined}
                        />
                    </div>
                    <AnimateHeight
                        id="record-patient-dropdown"
                        className="record-dropdown-animate-height"
                        duration={420}
                        height={patient.open ? 'auto' : 0}
                        easing="cubic-bezier(0.33, 1, 0.68, 1)"
                        animateOpacity
                    >
                        <PatientSelection
                            selected={patient.selected}
                            selectPatient={selectPatient}
                            data={patient.data}
                            addNewPatient={addNewPatient}
                        />
                    </AnimateHeight>
                </div>
            </Box>

            {patient.selected && (
                <SessionDetailsCard
                    values={sessionTags}
                    onChange={handleSessionTagChange}
                    suggestions={tagMemory}
                    open={sessionDetailsOpen}
                    onToggle={() => setSessionDetailsOpen((prev) => !prev)}
                    disabled={actionsLocked}
                />
            )}

            <div className="trials-list">
                {(!calibrationEnabled || calibration.selected) && patient.selected && (
                    <button
                        onClick={addNewTrial}
                        className="new-trial-btn svg-btn"
                        style={{ 
                            opacity: (!device.finishedChecking || !device.devicesFound || actionsLocked) ? 0.5 : 1,
                            cursor: (!device.finishedChecking || !device.devicesFound || actionsLocked) ? 'not-allowed' : 'pointer'
                        }}
                        disabled={!device.finishedChecking || !device.devicesFound || actionsLocked}
                    >
                        <Icon icon="mingcute:add-fill" />
                        New Trial
                    </button>
                )}
                <AnimateHeight
                    className="record-session-exit"
                    duration={SESSION_LEAVE_MS}
                    height={sessionExit ? 0 : 'auto'}
                    easing="cubic-bezier(0.33, 1, 0.68, 1)"
                    animateOpacity
                    onHeightAnimationEnd={onSessionExitEnd}
                >
                {shownTrials.map((el) => (
                    <RecordingPanel
                        key={el.localId}
                        trial={el}
                        disabled={
                            !!sessionExit ||
                            (actionsLocked && !el.isLoading) ||
                            (shownTrials.some(trial => trial.isRecording || trial.isLoading) && !el.isRecording && !el.isLoading)
                        }
                        startRecord={() => startRecord(el.localId)}
                        stopRecord={() => stopRecord(el.localId)}
                        removeTrial={() => removeTrial(el.localId)}
                        tagIdPrefix={`trial-${el.localId}-tag`}
                        tagsExpanded={expandedTrialTags === el.localId}
                        onToggleTags={() => setExpandedTrialTags((prev) => (prev === el.localId ? null : el.localId))}
                        onTagChange={(key, value) => handleTrialTagChange(el.localId, key, value)}
                        tagSuggestions={tagMemory}
                    />
                ))}
                {sessionFinished && (
                <button
                    className={`blue-button ${processing || actionsLocked || sessionExit || startingSession ? "disabled" : ""}`}
                    onClick={processAllRecordings}
                    disabled={processing || actionsLocked || !!sessionExit || startingSession}
                >
                {downloadsComplete ? "Start new recording" : (processing ? "Uploading..." : "Upload to server")}
                </button>
                )}
                </AnimateHeight>
                <Box>
                    <Dialog
                        TransitionComponent={Fade}
                        transitionDuration={{ enter: 260, exit: 180 }}
                        open={openDialog}
                        onClose={() => setOpenDialog(false)}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">{"Notification"}</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                {dialogMessage}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <CommonButton onClick={() => setOpenDialog(false)}>Close</CommonButton>
                        </DialogActions>
                    </Dialog>
                </Box>
            </div>
        </div>
    );
}

export default RecordPage;